import { SiteApiItem } from "../../site-listings/services/siteListingConfig";
import { RegistryPage } from "../registry/sitePageRegistry";
import { pageLabel, SEOForm } from "../types";

export interface SEOPrepopulationOptions {
  pageName: string;
  site: SiteApiItem | null;
  pageDefinition?: RegistryPage;
}

/**
 * Ensures a text string falls within [min, max] character bounds.
 * If shorter than min, appends safe filler words.
 * If longer than max, trims cleanly at word boundary.
 */
function fitBounds(text: string, min: number, max: number, filler: string = ""): string {
  let result = text.trim();
  if (result.length < min && filler) {
    result = `${result} ${filler}`.trim();
  }
  if (result.length > max) {
    const trimmed = result.slice(0, max - 3);
    const lastSpace = trimmed.lastIndexOf(" ");
    result = `${lastSpace > min ? trimmed.slice(0, lastSpace) : trimmed}...`;
  }
  return result;
}

/**
 * Determines whether a page should be treated as dynamic (record-level template)
 * vs static (site-level page).
 */
export function isDynamicPage(
  pageName: string,
  pageDefinition?: RegistryPage,
): boolean {
  if (pageDefinition?.isDynamic !== undefined) {
    return Boolean(pageDefinition.isDynamic);
  }
  if (pageDefinition?.variables && pageDefinition.variables.length > 0) {
    return true;
  }
  const normalized = pageName.toLowerCase().trim();
  return (
    normalized.includes("stream") ||
    normalized.includes("memorial") ||
    normalized.includes("detail") ||
    normalized.includes("single") ||
    normalized === "blog_post" ||
    normalized === "product"
  );
}

/**
 * Returns a fully prepopulated SEOForm with optimal values
 * tailored for either dynamic (merge tags) or static pages.
 */
export function getPrepopulatedSEOForm({
  pageName,
  site,
  pageDefinition,
}: SEOPrepopulationOptions): SEOForm {
  const siteName = site?.name?.trim() || "MMS";
  const label = pageDefinition?.label || pageLabel(pageName);
  const normalizedKey = (pageDefinition?.key || pageName || "").toLowerCase().trim();
  const isDynamic = isDynamicPage(normalizedKey, pageDefinition);
  const variables = pageDefinition?.variables || [];

  const defaultOgImage = site?.domain
    ? `https://${site.domain}/og-image.jpg`
    : "/images/og-image.jpg";

  if (isDynamic) {
    return getDynamicPageDefaults({
      normalizedKey,
      label,
      siteName,
      variables,
      defaultOgImage,
      pageName,
    });
  }

  return getStaticPageDefaults({
    normalizedKey,
    label,
    siteName,
    defaultOgImage,
    pageName,
  });
}

function getDynamicPageDefaults({
  normalizedKey,
  siteName,
  variables,
  defaultOgImage,
  pageName,
}: {
  normalizedKey: string;
  label: string;
  siteName: string;
  variables: { key: string; label: string; sample: string }[];
  defaultOgImage: string;
  pageName: string;
}): SEOForm {
  const hasDeceased = variables.some((v) => v.key.includes("deceased") || v.key.includes("name"));
  const hasDate = variables.some((v) => v.key.includes("date") || v.key.includes("time"));
  const imageVar = variables.find((v) =>
    v.key.includes("image") || v.key.includes("photo") || v.key.includes("banner") || v.key.includes("thumb")
  );

  const imageToken = imageVar ? `{{${imageVar.key}}}` : defaultOgImage;

  // 1. Memorial / Funeral / Stream Page dynamic preset
  if (
    normalizedKey.includes("stream") ||
    normalizedKey.includes("memorial") ||
    normalizedKey.includes("funeral") ||
    hasDeceased
  ) {
    const rawTitle = `{{deceased_name}} - Memorial & Funeral Stream | ${siteName}`;
    const metaTitle = fitBounds(rawTitle, 30, 60);

    const dateToken = hasDate ? " on {{stream_date}}" : "";
    const rawDesc = `Watch the live memorial stream and funeral service for {{deceased_name}}${dateToken}. Send your condolences and view tributes online.`;
    const metaDescription = fitBounds(
      rawDesc,
      120,
      160,
      "Live broadcast connects family and friends worldwide.",
    );

    const focusKeyword = "funeral stream";
    const ogTitle = `{{deceased_name}} Memorial Stream`;
    const ogDescription = `Watch the live memorial service for {{deceased_name}} online.`;

    return {
      pageName: pageName === "new" ? "" : pageName,
      metaTitle,
      metaDescription,
      focusKeyword,
      relatedKeywords: [
        "memorial stream",
        "funeral live stream",
        "celebration of life",
        "online condolences",
      ],
      metaKeywords: "memorial stream, funeral live stream, celebration of life",
      canonicalUrl: "",
      noindex: false,
      nofollow: false,
      ogTitle,
      ogType: "video.other",
      ogDescription,
      ogImageUrl: imageToken,
      twitterCardType: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImageUrl: imageToken,
      twitterSite: "",
      twitterCreator: "",
      schemaType: "BroadcastEvent",
      isActive: true,
    };
  }

  // 2. Dynamic Blog Post preset
  if (normalizedKey.includes("blog") || normalizedKey.includes("article") || normalizedKey.includes("post")) {
    const rawTitle = `{{post_title}} | ${siteName} Blog`;
    const metaTitle = fitBounds(rawTitle, 30, 60);

    const rawDesc = `Read {{post_title}} on ${siteName}. Discover insights, stories, and helpful guides from our team. Read the full article online.`;
    const metaDescription = fitBounds(rawDesc, 120, 160, "Stay informed with our latest news and resources.");

    const focusKeyword = "blog";
    const ogTitle = `{{post_title}}`;
    const ogDescription = `Read {{post_title}} on ${siteName}. Discover insights and guides.`;

    return {
      pageName: pageName === "new" ? "" : pageName,
      metaTitle,
      metaDescription,
      focusKeyword,
      relatedKeywords: ["blog", "article", "memorial guides", "news"],
      metaKeywords: "blog, article, insights, guides",
      canonicalUrl: "",
      noindex: false,
      nofollow: false,
      ogTitle,
      ogType: "article",
      ogDescription,
      ogImageUrl: imageToken,
      twitterCardType: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImageUrl: imageToken,
      twitterSite: "",
      twitterCreator: "",
      schemaType: "BlogPosting",
      isActive: true,
    };
  }

  // 3. Generic Dynamic Page fallback
  const firstVar = variables[0]?.key || "item_title";
  const rawTitle = `{{${firstVar}}} - Live Details | ${siteName}`;
  const metaTitle = fitBounds(rawTitle, 30, 60);

  const rawDesc = `View dynamic information and details for {{${firstVar}}} on ${siteName}. Access live streaming, service updates, and interactive features online.`;
  const metaDescription = fitBounds(rawDesc, 120, 160, "Available for friends and family worldwide.");

  const focusKeyword = "live details";
  const ogTitle = `{{${firstVar}}} | ${siteName}`;
  const ogDescription = `View dynamic information and details for {{${firstVar}}} on ${siteName}.`;

  return {
    pageName: pageName === "new" ? "" : pageName,
    metaTitle,
    metaDescription,
    focusKeyword,
    relatedKeywords: ["live details", "streaming", "updates", siteName.toLowerCase()],
    metaKeywords: `live details, streaming, ${siteName.toLowerCase()}`,
    canonicalUrl: "",
    noindex: false,
    nofollow: false,
    ogTitle,
    ogType: "website",
    ogDescription,
    ogImageUrl: imageToken,
    twitterCardType: "summary_large_image",
    twitterTitle: ogTitle,
    twitterDescription: ogDescription,
    twitterImageUrl: imageToken,
    twitterSite: "",
    twitterCreator: "",
    schemaType: "WebPage",
    isActive: true,
  };
}

function getStaticPageDefaults({
  normalizedKey,
  label,
  siteName,
  defaultOgImage,
  pageName,
}: {
  normalizedKey: string;
  label: string;
  siteName: string;
  defaultOgImage: string;
  pageName: string;
}): SEOForm {
  // 1. Home Page
  if (normalizedKey === "home" || normalizedKey === "" || normalizedKey === "index") {
    const rawTitle = `${siteName} - Live Streaming & Memorial Services`;
    const metaTitle = fitBounds(rawTitle, 30, 60);

    const rawDesc = `Providing high-quality live streaming services for funerals, memorials, and special events. Connect family and friends worldwide with ${siteName}.`;
    const metaDescription = fitBounds(rawDesc, 120, 160);

    const focusKeyword = "live streaming";
    const ogTitle = `${siteName} - Live Streaming Services`;
    const ogDescription = `High-quality live streaming services for funerals, memorials, and special events worldwide.`;

    return {
      pageName: pageName === "new" ? "" : pageName,
      metaTitle,
      metaDescription,
      focusKeyword,
      relatedKeywords: [
        "live streaming",
        "funeral streaming",
        "memorial broadcast",
        "event streaming",
      ],
      metaKeywords: "live streaming, funeral streaming, memorial broadcast, event streaming",
      canonicalUrl: "",
      noindex: false,
      nofollow: false,
      ogTitle,
      ogType: "website",
      ogDescription,
      ogImageUrl: defaultOgImage,
      twitterCardType: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImageUrl: defaultOgImage,
      twitterSite: "",
      twitterCreator: "",
      schemaType: "Organization",
      isActive: true,
    };
  }

  // 2. About Page
  if (normalizedKey === "about" || normalizedKey.includes("about")) {
    const rawTitle = `About Us | ${siteName} Live Streaming Team`;
    const metaTitle = fitBounds(rawTitle, 30, 60);

    const rawDesc = `Learn about ${siteName} and our mission to deliver respectful, professional live broadcasting for memorials, funerals, and special family gatherings.`;
    const metaDescription = fitBounds(rawDesc, 120, 160);

    const focusKeyword = "about us";
    const ogTitle = `About Us - ${siteName}`;
    const ogDescription = `Learn about ${siteName} and our mission to provide dignified live memorial broadcasting.`;

    return {
      pageName: pageName === "new" ? "" : pageName,
      metaTitle,
      metaDescription,
      focusKeyword,
      relatedKeywords: ["about us", "our mission", "streaming team", "company story"],
      metaKeywords: "about us, our mission, streaming team, company story",
      canonicalUrl: "",
      noindex: false,
      nofollow: false,
      ogTitle,
      ogType: "website",
      ogDescription,
      ogImageUrl: defaultOgImage,
      twitterCardType: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImageUrl: defaultOgImage,
      twitterSite: "",
      twitterCreator: "",
      schemaType: "AboutPage",
      isActive: true,
    };
  }

  // 3. Contact Page
  if (normalizedKey === "contact" || normalizedKey.includes("contact")) {
    const rawTitle = `Contact Us | Get in Touch with ${siteName}`;
    const metaTitle = fitBounds(rawTitle, 30, 60);

    const rawDesc = `Get in touch with ${siteName} for live streaming bookings, consultation inquiries, and support. Our team is available to assist your family and event.`;
    const metaDescription = fitBounds(rawDesc, 120, 160);

    const focusKeyword = "contact us";
    const ogTitle = `Contact Us - ${siteName}`;
    const ogDescription = `Get in touch with ${siteName} for event streaming inquiries and customer support.`;

    return {
      pageName: pageName === "new" ? "" : pageName,
      metaTitle,
      metaDescription,
      focusKeyword,
      relatedKeywords: ["contact us", "customer support", "booking inquiry", "get in touch"],
      metaKeywords: "contact, support, streaming inquiry, booking",
      canonicalUrl: "",
      noindex: false,
      nofollow: false,
      ogTitle,
      ogType: "website",
      ogDescription,
      ogImageUrl: defaultOgImage,
      twitterCardType: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImageUrl: defaultOgImage,
      twitterSite: "",
      twitterCreator: "",
      schemaType: "ContactPage",
      isActive: true,
    };
  }

  // 4. Chema Page
  if (normalizedKey === "chema" || normalizedKey.includes("chema")) {
    const rawTitle = `Chema Bereavement Contributions | ${siteName}`;
    const metaTitle = fitBounds(rawTitle, 30, 60);

    const rawDesc = `Support grieving families securely with Chema bereavement contributions on ${siteName}. Transparent digital collections for compassionate community funds.`;
    const metaDescription = fitBounds(rawDesc, 120, 160);

    const focusKeyword = "bereavement contributions";
    const ogTitle = `Chema Contributions - ${siteName}`;
    const ogDescription = `Support grieving families securely with Chema bereavement contributions on ${siteName}.`;

    return {
      pageName: pageName === "new" ? "" : pageName,
      metaTitle,
      metaDescription,
      focusKeyword,
      relatedKeywords: [
        "bereavement contributions",
        "funeral fundraising",
        "community fund",
        "chema donations",
      ],
      metaKeywords: "chema, contributions, bereavement support, community funds",
      canonicalUrl: "",
      noindex: false,
      nofollow: false,
      ogTitle,
      ogType: "website",
      ogDescription,
      ogImageUrl: defaultOgImage,
      twitterCardType: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImageUrl: defaultOgImage,
      twitterSite: "",
      twitterCreator: "",
      schemaType: "WebPage",
      isActive: true,
    };
  }

  // 5. Blog List Page
  if (normalizedKey === "blog_list" || normalizedKey === "blog") {
    const rawTitle = `Guides & Resources | ${siteName} Memorial News`;
    const metaTitle = fitBounds(rawTitle, 30, 60);

    const rawDesc = `Explore guides, articles, and practical advice on live memorial streaming, bereavement support, and event planning from the team at ${siteName}.`;
    const metaDescription = fitBounds(rawDesc, 120, 160);

    const focusKeyword = "memorial news";
    const ogTitle = `${siteName} - Guides & Resources`;
    const ogDescription = `Articles and guides on live memorial streaming and event planning.`;

    return {
      pageName: pageName === "new" ? "" : pageName,
      metaTitle,
      metaDescription,
      focusKeyword,
      relatedKeywords: ["articles", "guides", "memorial advice", "industry news"],
      metaKeywords: "guides, articles, memorial streaming, advice",
      canonicalUrl: "",
      noindex: false,
      nofollow: false,
      ogTitle,
      ogType: "website",
      ogDescription,
      ogImageUrl: defaultOgImage,
      twitterCardType: "summary_large_image",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImageUrl: defaultOgImage,
      twitterSite: "",
      twitterCreator: "",
      schemaType: "BlogPosting",
      isActive: true,
    };
  }

  // 6. Generic Static Page fallback
  const rawTitle = `${label} | ${siteName} Official Website`;
  const metaTitle = fitBounds(rawTitle, 30, 60);

  const rawDesc = `Discover information and services on the ${label} page of ${siteName}. Explore our comprehensive offerings and reach out for assistance anytime.`;
  const metaDescription = fitBounds(rawDesc, 120, 160);

  const focusKeyword = label.toLowerCase();
  const ogTitle = `${label} - ${siteName}`;
  const ogDescription = `Discover information and services on the ${label} page of ${siteName}.`;

  return {
    pageName: pageName === "new" ? "" : pageName,
    metaTitle,
    metaDescription,
    focusKeyword,
    relatedKeywords: [label.toLowerCase(), siteName.toLowerCase(), "services"],
    metaKeywords: `${label.toLowerCase()}, ${siteName.toLowerCase()}, services`,
    canonicalUrl: "",
    noindex: false,
    nofollow: false,
    ogTitle,
    ogType: "website",
    ogDescription,
    ogImageUrl: defaultOgImage,
    twitterCardType: "summary_large_image",
    twitterTitle: ogTitle,
    twitterDescription: ogDescription,
    twitterImageUrl: defaultOgImage,
    twitterSite: "",
    twitterCreator: "",
    schemaType: "WebPage",
    isActive: true,
  };
}
